import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OrganizationUserGroupDto, UserGroupMemberDto } from 'src/app/data-transfer/entities/user-group-entities/organization-user-group-dto';
import { DialogActions } from 'src/app/model/dictionary';
import { AdminDialogResult } from '../admin-dialog-result';
import { getUserDisplayName } from '../organization-user.util';

export interface AdminGroupMemberDialogData {
  group: OrganizationUserGroupDto;
  users: UserGroupMemberDto[];
}

export type AdminGroupMemberDialogResult = AdminDialogResult<{ added: string[]; removed: string[] }>;

interface MemberRow {
  user: UserGroupMemberDto;
  name: string;
}

@Component({
  selector: 'app-admin-group-member-dialog',
  templateUrl: './admin-group-member-dialog.component.html',
  styleUrls: ['./admin-group-member-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminGroupMemberDialogComponent {
  private readonly dialogRef = inject<MatDialogRef<AdminGroupMemberDialogComponent, AdminGroupMemberDialogResult>>(MatDialogRef);
  protected readonly data = inject<AdminGroupMemberDialogData>(MAT_DIALOG_DATA);

  private readonly initialMemberIds: ReadonlySet<string> = new Set(this.data.users.filter(user => user.userGroupIds.includes(this.data.group.id)).map(user => user.id));
  protected readonly memberIds = signal<ReadonlySet<string>>(new Set(this.initialMemberIds));

  protected readonly searchControl = new FormControl<string>('', { nonNullable: true });
  private readonly searchTerm = toSignal(this.searchControl.valueChanges, { initialValue: this.searchControl.value });
  private readonly rows: MemberRow[] = this.data.users.map(user => ({ user, name: getUserDisplayName(user) }));

  protected readonly filteredRows = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    if (!term) return this.rows;
    return this.rows.filter(row => row.name.toLowerCase().includes(term) || row.user.email.toLowerCase().includes(term));
  });

  protected toggleMembership(user: UserGroupMemberDto): void {
    const memberIds = new Set(this.memberIds());
    if (!memberIds.delete(user.id)) {
      memberIds.add(user.id);
    }
    this.memberIds.set(memberIds);
  }

  protected save(): void {
    const memberIds = this.memberIds();
    this.dialogRef.close({
      event: DialogActions.CONFIRM,
      data: {
        added: [...memberIds].filter(id => !this.initialMemberIds.has(id)),
        removed: [...this.initialMemberIds].filter(id => !memberIds.has(id)),
      },
    });
  }

  protected closeDialog(): void {
    this.dialogRef.close({ event: DialogActions.REJECT });
  }
}
